import * as React from 'react';
import styled, { useTheme } from 'styled-components/macro';

import * as theme from 'lib/style/theme';

export type ComponentProps = {
  disabled?: boolean;
  onClick?: (
    e?:
      | React.SyntheticEvent
      | React.SyntheticEvent<Element, Event>
      | React.MouseEvent
  ) => void;
  text: string;
  isFullWidth?: boolean;
  danger?: boolean;
  type?: 'button' | 'submit';
  textPadding?: number | string;
  icon?: any;
  margin?: number | string;
  borderRadius?: number;
  fontWeight?: number;
  fontSize?: number | string;
  fontFamily?: string;
  iconPadding?: number | string;
  iconMargin?: number | string;
  iconFontSize?: number | string;
  iconOpacity?: number;
  backgroundColor?: string;
  border?: string;
  textColor?: string;
  iconColor?: string;
  className?: string;
  height?: number | string;
  width?: number | string;
  center?: boolean;
  title?: string;
  isVisible?: boolean;
  hoverBackgroundColor?: string;
  hoverTextColor?: string;
  minWidth?: number | string;
  padding?: number | string;
  iconPlacement?: 'left' | 'right';
  dataCy?: string;
  hoverBorder?: string;
};

type ButtonProps = {
  isFullWidth?: boolean;
  danger?: boolean;
  margin?: number | string;
  borderRadius?: number;
  fontWeight?: number;
  fontSize?: number | string;
  border?: string;
  backgroundColor?: string;
  height?: number | string;
  width?: number | string;
  minWidth?: number | string;
  isVisible?: boolean;
  hoverBackgroundColor?: string;
  hoverBorder?: string;
  padding?: number | string;
};

type TextProps = {
  padding?: number | string;
  fontSize?: number | string;
  fontFamily?: string;
  color?: string;
  withIcon?: boolean;
  center?: boolean;
  hoverTextColor?: string;
};

type IconProps = {
  margin?: number | string;
  padding?: number | string;
  fontSize?: number | string;
  opacity?: number;
  color?: string;
  center?: boolean;
};

const Button = styled.button<ButtonProps>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: ${props => (props.padding ? props.padding : '0')};
  color: ${theme.palette.primaryDarkBlue};
  ${props =>
    props.fontWeight ? `font-weight:${props.fontWeight}` : theme.fontBold700};
  background: ${props =>
    props.danger
      ? theme.palette.buttonDanger
      : props.theme.colors.primaryColor};
  border-radius: ${props => (props.borderRadius ? props.borderRadius : '0')}px;
  background-color: ${props =>
    props.backgroundColor
      ? props.backgroundColor
      : props.theme.colors.primaryColor};
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  opacity: ${props => props.disabled && '.4'};
  overflow: hidden;
  transition: 0.3s all;
  visibility: ${props => (props.isVisible == false ? 'hidden' : 'visible')};
  width: ${props => (props.isFullWidth && '100%') || 'auto'};
  min-width: ${props => props.minWidth || 'auto'};
  ${props => {
    if (props.isFullWidth && props.height === undefined) return 'height:100%';
    if (typeof props.height === 'number') return `height:${props.height}px`;
    if (typeof props.height === 'string') return `height:${props.height}`;
    return 'height: auto';
  }};


${props => {
  if (typeof props.width === 'string') {
    return `width:${props.width};`;
  }
  if (typeof props.width === 'number') {
    return `width: ${props.width}px;`;
  }
}}

  ${props => {
    if (typeof props.margin === 'string') {
      return `margin: ${props.margin};`;
    }
    if (typeof props.margin === 'number') {
      return `margin: ${props.margin}px;`;
    }
    return 'margin: 0;';
  }}

  ${props => {
    if (props.backgroundColor) {
      return `background-color: ${props.backgroundColor};`;
    }
  }}

  ${props => {
    if (props.border) {
      return `border: ${props.border};`;
    }
    return 'border: none;';
  }}

  ${theme.mediaQueryMinWidth.mb} {
    &:hover:not(:active) {
      ${props => {
        if (props.disabled) {
          return '';
        }

        if (props.hoverBackgroundColor) {
          return `background: ${props.hoverBackgroundColor};`;
        }

        if (!props.disabled && !props.danger && !props.backgroundColor) {
          return `background: ${theme.palette.gray60};`;
        }
        if (props.danger) {
          return `background: ${theme.palette.buttonDangerHover};`;
        }
        if (props.hoverBorder) {
          return `border: ${props.hoverBorder};`;
        }
        return `opacity: 0.6;`;
      }}
    }
  }

  &:focus {
    outline: 0;
  }
`;

const Text = styled.div<TextProps>`
  display: flex;
  align-items: center;
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: center;
  font-family: ${props => {
    if (props.fontFamily) {
      return `${props.fontFamily}`;
    }
    return `'Work Sans', sans-serif`;
  }};

  ${props => {
    if (!props.center) {
      if (props.withIcon) {
        return 'width: 80%;';
      }
      return 'width: 100%;';
    }
  }}

  ${props => {
    if (typeof props.padding === 'string') {
      return `padding:${props.padding};`;
    }
    if (typeof props.padding === 'number') {
      return `padding: 16px ${props.padding}px 16px ${props.padding}px;`;
    }
    return 'padding: 16px;';
  }}

  ${props => {
    if (typeof props.fontSize === 'string') {
      return `font-size:${props.fontSize};`;
    }
    if (typeof props.fontSize === 'number') {
      return `font-size:${props.fontSize}px;`;
    }
    return `font-size:${theme.fontSizes.md};`;
  }}

  ${props => {
    if (props.color) {
      return `color:${props.color};`;
    }
  }}

  ${theme.mediaQueryMinWidth.mb} {
    ${props => {
      if (props.hoverTextColor) {
        return `&:hover:not(:active) {
          color:${props.hoverTextColor};
        }`;
      }
    }}
  }
`;

const IconWrapper = styled.div<IconProps>`
  display: flex;
  width: ${props => (props.center ? 'auto' : '20%')};

  ${props => {
    if (typeof props.margin === 'string') {
      return `margin: ${props.margin};`;
    }
    if (typeof props.margin === 'number') {
      return `margin: ${props.margin}px;`;
    }
    return 'margin: 0 4px 0 0;';
  }}

  ${props => {
    if (props.color) {
      return `
      color:${props.color};
      opacity: ${typeof props.opacity !== 'undefined' ? props.opacity : '0.5'};
      svg > g > path {
        fill: ${props.color};
      }
      `;
    }
  }}

  ${props => {
    if (typeof props.padding === 'string') {
      return `padding:${props.padding};`;
    }
    if (typeof props.padding === 'number') {
      return `padding: 0 0 0 ${props.padding}px;`;
    }
    return 'padding: 0 0 0 16px;';
  }}

  ${props => {
    if (typeof props.fontSize === 'string') {
      return `font-size:${props.fontSize};`;
    }
    if (typeof props.fontSize === 'number') {
      return `font-size:${props.fontSize}px;`;
    }
    return `font-size: ${theme.fontSizes.xl};`;
  }}
`;

export const PrimaryButton = ({
  disabled = false,
  onClick,
  text,
  isFullWidth = false,
  danger = false,
  type = 'button',
  textPadding,
  margin,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  icon,
  iconFontSize,
  iconPadding,
  iconMargin,
  iconColor,
  iconOpacity,
  backgroundColor,
  textColor,
  border,
  hoverBorder,
  className = '',
  height,
  width,
  center,
  title = '',
  isVisible = true,
  hoverBackgroundColor = '',
  hoverTextColor = '',
  minWidth,
  padding = '',
  iconPlacement = 'left',
  dataCy,
}: ComponentProps) => {
  const themes = useTheme();
  return (
    <Button
      disabled={disabled}
      onClick={onClick}
      danger={danger}
      type={type}
      isFullWidth={isFullWidth}
      margin={margin}
      borderRadius={borderRadius}
      fontWeight={fontWeight}
      border={border}
      backgroundColor={backgroundColor || themes.colors.primaryColor}
      className={className}
      height={height}
      width={width}
      minWidth={minWidth}
      title={title}
      isVisible={isVisible}
      hoverBackgroundColor={hoverBackgroundColor}
      padding={padding}
      data-cy={dataCy}
      hoverBorder={hoverBorder}
    >
      {icon && iconPlacement === 'left' && (
        <IconWrapper
          className='icon-wrapper'
          padding={iconPadding}
          fontSize={iconFontSize}
          opacity={iconOpacity}
          color={iconColor}
          margin={iconMargin}
          center={center}
        >
          {icon}
        </IconWrapper>
      )}
      {text !== '' && (
        <Text
          className='text'
          padding={textPadding}
          fontSize={fontSize}
          fontFamily={fontFamily}
          color={textColor}
          hoverTextColor={hoverTextColor}
          center={center}
          withIcon={typeof icon !== 'undefined'}
        >
          {text}
        </Text>
      )}
      {icon && iconPlacement === 'right' && (
        <IconWrapper
          className='icon-wrapper'
          padding={iconPadding}
          fontSize={iconFontSize}
          opacity={iconOpacity}
          color={iconColor}
          margin={iconMargin}
          center={center}
        >
          {icon}
        </IconWrapper>
      )}
    </Button>
  );
};
